import { useEffect, useCallback, useRef } from 'react';

interface TableNavigationOptions {
  rows: number;
  cols: number;
  onCellFocus?: (row: number, col: number) => void;
  onEnter?: (row: number, col: number) => void;
  onEscape?: () => void;
}

/**
 * Hook for keyboard navigation within tables
 * Supports arrow keys to move between cells and Enter to commit
 */
export function useTableNavigation({
  rows,
  cols,
  onCellFocus,
  onEnter,
  onEscape
}: TableNavigationOptions) {
  const currentPosition = useRef({ row: 0, col: 0 });

  const moveToCell = useCallback((row: number, col: number) => {
    // Clamp to valid bounds
    const newRow = Math.max(0, Math.min(row, rows - 1));
    const newCol = Math.max(0, Math.min(col, cols - 1));
    
    currentPosition.current = { row: newRow, col: newCol };
    onCellFocus?.(newRow, newCol);
  }, [rows, cols, onCellFocus]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const target = event.target as HTMLElement;
    
    // Only handle navigation when in table inputs
    if (!target.closest('.table-navigation-container')) {
      return;
    }

    // Skip if not in an input field
    if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
      return;
    }

    const { row, col } = currentPosition.current;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        moveToCell(row, col - 1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        moveToCell(row, col + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveToCell(row - 1, col);
        break;
      case 'ArrowDown':
        event.preventDefault();
        moveToCell(row + 1, col);
        break;
      case 'Enter':
        event.preventDefault();
        onEnter?.(row, col);
        break;
      case 'Escape':
        event.preventDefault();
        onEscape?.();
        break;
    }
  }, [moveToCell, onEnter, onEscape]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return {
    currentPosition: currentPosition.current,
    moveToCell
  };
}

